import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SUBJECTS, HOMEWORK_STATUS, subjectStyle } from "@/lib/constants";
import { Plus, Trash2, Star, X, CheckCircle2, Circle, Loader, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const empty = { subject: "Mathématiques", title: "", description: "", due_date: "", important: false };
const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "a_faire", label: "À faire" },
  { key: "en_cours", label: "En cours" },
  { key: "termine", label: "Terminés" },
];

function daysUntil(due) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(due + "T00:00:00") - today) / 86400000);
}

export default function Homework() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/homework").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/homework", form);
      toast.success("Devoir ajouté");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cycleStatus = async (hw) => {
    const next = { a_faire: "en_cours", en_cours: "termine", termine: "a_faire" }[hw.status];
    try {
      const { data } = await api.put(`/homework/${hw.id}`, { status: next });
      if (next === "termine") toast.success("Devoir terminé, bravo !");
      setItems(items.map((i) => (i.id === hw.id ? data : i)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleImportant = async (hw) => {
    try {
      const { data } = await api.put(`/homework/${hw.id}`, { important: !hw.important });
      setItems(items.map((i) => (i.id === hw.id ? data : i)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/homework/${id}`);
      toast.success("Devoir supprimé");
      setItems(items.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = items.filter((i) =>
    (filter === "all" || i.status === filter) && (subjectFilter === "all" || i.subject === subjectFilter)
  );

  const urgent = items.filter((i) => i.status !== "termine" && (i.important || daysUntil(i.due_date) <= 1));

  const StatusIcon = { a_faire: Circle, en_cours: Loader, termine: CheckCircle2 };

  return (
    <div className="space-y-6" data-testid="homework-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Devoirs</h1>
          <p className="text-slate-500 text-sm sm:text-base mt-1">Plus jamais de « j'ai oublié mon devoir ».</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="add-homework-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200"
        >
          <Plus className="h-4 w-4" /> Ajouter un devoir
        </button>
      </div>

      {urgent.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 flex items-start gap-3" data-testid="homework-reminder-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Rappel : {urgent.length} devoir{urgent.length > 1 ? "s" : ""} urgent{urgent.length > 1 ? "s" : ""} !</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {urgent.slice(0, 3).map((u) => u.title).join(" · ")}{urgent.length > 3 ? "…" : ""}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-1.5 p-1 bg-white border border-slate-200 rounded-full">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`homework-filter-${f.key}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${filter === f.key ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          value={subjectFilter}
          onChange={(e) => setSubjectFilter(e.target.value)}
          data-testid="homework-subject-filter"
          className="px-4 py-2 rounded-full border border-slate-200 text-xs font-semibold bg-white text-slate-700"
        >
          <option value="all">Toutes les matières</option>
          {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-2xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80" data-testid="homework-empty">
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Rien à afficher ici.</p>
          <p className="text-sm text-slate-400 mt-1">Ajoute un devoir pour commencer à t'organiser.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((hw) => {
            const d = daysUntil(hw.due_date);
            const done = hw.status === "termine";
            const Icon = StatusIcon[hw.status];
            return (
              <li
                key={hw.id}
                data-testid={`homework-item-${hw.id}`}
                className={`bg-white rounded-2xl border p-5 flex flex-wrap items-center gap-4 shadow-sm ${done ? "border-emerald-200 opacity-70" : "border-slate-200/80"}`}
              >
                <button onClick={() => cycleStatus(hw)} data-testid={`homework-status-${hw.id}`} className="shrink-0">
                  <Icon className={`h-6 w-6 ${done ? "text-emerald-500" : hw.status === "en_cours" ? "text-amber-500" : "text-slate-300 hover:text-indigo-400"}`} />
                </button>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${subjectStyle(hw.subject)}`}>{hw.subject}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${HOMEWORK_STATUS[hw.status].classes}`} data-testid={`homework-status-label-${hw.id}`}>
                      {HOMEWORK_STATUS[hw.status].label}
                    </span>
                    {hw.important && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 flex items-center gap-1">
                        <Star className="h-3 w-3 fill-current" /> Important
                      </span>
                    )}
                  </div>
                  <p className={`text-sm font-semibold mt-1.5 ${done ? "line-through text-slate-400" : "text-slate-900"}`}>{hw.title}</p>
                  {hw.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{hw.description}</p>}
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full shrink-0 ${
                  done ? "bg-slate-100 text-slate-500" : d < 0 ? "bg-rose-100 text-rose-700" : d <= 1 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                }`} data-testid={`homework-due-${hw.id}`}>
                  {done ? "Terminé" : d < 0 ? "En retard" : d === 0 ? "Pour aujourd'hui" : d === 1 ? "Pour demain" : `J-${d}`}
                </span>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => toggleImportant(hw)} data-testid={`homework-star-${hw.id}`} className={`p-2 rounded-lg transition-colors duration-200 ${hw.important ? "text-rose-500 bg-rose-50" : "text-slate-300 hover:text-rose-400 hover:bg-rose-50"}`}>
                    <Star className={`h-4 w-4 ${hw.important ? "fill-current" : ""}`} />
                  </button>
                  <button onClick={() => remove(hw.id)} data-testid={`homework-delete-${hw.id}`} className="p-2 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-colors duration-200">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="homework-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900">Nouveau devoir</h2>
              <button onClick={() => setOpen(false)} data-testid="homework-modal-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Matière</label>
                <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="homework-subject-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Titre</label>
                <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="homework-title-input" placeholder="Ex : Exercices 12 et 13 p.45" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description (optionnel)</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="homework-description-input" rows={2} placeholder="Détails, pages, consignes…" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm resize-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date limite</label>
                <input type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="homework-due-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.important} onChange={(e) => setForm({ ...form, important: e.target.checked })} data-testid="homework-important-checkbox" className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                <span className="text-sm text-slate-700">Devoir important (rappel renforcé)</span>
              </label>
              <button type="submit" data-testid="homework-submit-button" className="w-full py-3 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200">
                Ajouter le devoir
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
